import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useLanguage } from '../context/LanguageContext';
import { submitContact } from '../lib/api';
import { toast } from 'sonner';

export default function Contact() {
  const { t, language } = useLanguage();
  const isEs = language === 'es';
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    preferred_contact: 'email'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await submitContact(formData);
      
      // Send webhook to CRM (non-blocking)
      try {
        const crmPayload = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company || '',
          subject: formData.subject,
          message: formData.message,
          preferred_contact: formData.preferred_contact,
          source: 'website_contact_form',
          language: language,
          timestamp: new Date().toISOString()
        };
        
        fetch('https://crm.iqeslowvoltage.com/api/webhooks/chatbot', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(crmPayload)
        }).catch(err => console.log('CRM webhook error:', err));
      } catch (webhookError) {
        console.log('CRM webhook error:', webhookError);
      }
      
      toast.success(t('contact.success'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        preferred_contact: 'email'
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error sending message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-white" data-testid="contact-page">
      {/* Hero - EXACT Abacus colors */}
      <section className="relative py-16 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 51, 102, 0.867) 50%, rgba(0, 166, 81, 0.25) 100%)' }}>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-4">
              {t('contact.title')}
            </h1>
            <p className="text-lg text-white/70">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-4">
              <Card className="bg-white border border-gray-200 shadow-sm">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(0, 166, 81, 0.082)' }}>
                    <Phone className="w-6 h-6" style={{ color: 'rgb(0, 166, 81)' }} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold mb-1" style={{ color: 'rgb(31, 41, 55)' }}>Phone</h3>
                    <a href="tel:+17869813760" className="block hover:text-green-600 transition-colors" style={{ color: 'rgb(107, 114, 128)' }}>786-981-3760</a>
                    <p className="text-sm" style={{ color: 'rgb(156, 163, 175)' }}>Mon-Fri 8am-6pm EST</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200 shadow-sm">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(0, 166, 81, 0.082)' }}>
                    <Mail className="w-6 h-6" style={{ color: 'rgb(0, 166, 81)' }} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold mb-1" style={{ color: 'rgb(31, 41, 55)' }}>Email</h3>
                    <p style={{ color: 'rgb(107, 114, 128)' }}>info@iqeslowvoltage.com</p>
                    <p className="text-sm" style={{ color: 'rgb(156, 163, 175)' }}>24hr response time</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200 shadow-sm">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(0, 166, 81, 0.082)' }}>
                    <MapPin className="w-6 h-6" style={{ color: 'rgb(0, 166, 81)' }} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold mb-1" style={{ color: 'rgb(31, 41, 55)' }}>Service Area</h3>
                    <p style={{ color: 'rgb(107, 114, 128)' }}>Florida, USA</p>
                    <p style={{ color: 'rgb(107, 114, 128)' }}>Serving All Major Cities</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="lg:col-span-2 bg-white border border-gray-200 shadow-sm">
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-700 font-medium">{t('quote.name')} *</Label>
                      <Input
                        id="name"
                        required
                        placeholder={isEs ? 'Ingrese su nombre' : 'Enter your name'}
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-white border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-[#00A651] focus:ring-[#00A651]"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-700 font-medium">{t('quote.email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        placeholder={isEs ? 'correo@ejemplo.com' : 'email@example.com'}
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="bg-white border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-[#00A651] focus:ring-[#00A651]"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700 font-medium">{t('quote.phone')}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder={isEs ? '(XXX) XXX-XXXX' : '(XXX) XXX-XXXX'}
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="bg-white border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-[#00A651] focus:ring-[#00A651]"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-gray-700 font-medium">{t('quote.company')}</Label>
                      <Input
                        id="company"
                        placeholder={isEs ? 'Nombre de empresa (opcional)' : 'Company name (optional)'}
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        className="bg-white border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-[#00A651] focus:ring-[#00A651]"
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-gray-700 font-medium">{t('contact.subject')} *</Label>
                    <Input
                      id="subject"
                      required
                      placeholder={isEs ? 'Asunto de su mensaje' : 'Subject of your message'}
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      className="bg-white border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-[#00A651] focus:ring-[#00A651]"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-700 font-medium">{t('contact.message')} *</Label>
                    <Textarea
                      id="message"
                      required
                      placeholder={isEs ? 'Escriba su mensaje aquí...' : 'Write your message here...'}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="bg-white border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-[#00A651] focus:ring-[#00A651] min-h-[150px]"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">{t('contact.preferredContact')}</Label>
                    <RadioGroup
                      value={formData.preferred_contact}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_contact: value }))}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="email" id="email-pref" data-testid="contact-pref-email" />
                        <Label htmlFor="email-pref" className="font-normal cursor-pointer text-gray-600">{t('contact.emailMethod')}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="phone" id="phone-pref" data-testid="contact-pref-phone" />
                        <Label htmlFor="phone-pref" className="font-normal cursor-pointer text-gray-600">{t('contact.phoneMethod')}</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full py-6 text-white"
                    style={{ backgroundColor: '#00A651' }}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 w-4 h-4" />
                        {t('contact.send')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
